// apps/client/src/ui/screens/towns.tsx (M0-T17) — the towns phase screen: your towns, found a town, edit your character, sign out
import { useEffect } from 'react';
import { api } from '../../api/endpoints';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Button, Panel, Tag } from '../kit';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { towns: undefined } }

function Towns() {
  const t = useT();
  const towns = useGame((s) => s.me?.towns ?? null);
  const notice = useGame((s) => s.townsNotice);
  // Re-read the list on every visit (M2 playtest): membership changes while you were in a town (removed by the mayor, a town archived,
  // a new mayor) are only known to the server. The list shown meanwhile is the last one read.
  useEffect(() => {
    let live = true;
    void (async () => {
      try { const me = await api.me(); if (live && me) useGame.getState().setMe(me); }
      catch { /* offline: keep the last list */ }
    })();
    return () => { live = false; };
  }, []);
  return (
    <div className="layer-full hit" style={{ display: 'grid', placeItems: 'center' }}>
      <Panel title={t('towns.title')}>
        <div style={{ display: 'grid', gap: 12, width: 520, maxWidth: '100%' }}>
          {notice && (
            <div role="status" data-testid="towns-notice" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              padding: '10px 14px', borderRadius: 16, background: 'var(--cream-100)', fontWeight: 800 }}>
              <span>{t(notice.key, notice.params)}</span>
              <Button variant="quiet" onClick={() => useGame.getState().setTownsNotice(null)}>{t('common.ok')}</Button>
            </div>
          )}
          {!towns || towns.length === 0 ? <p style={{ margin: 0 }}>{t('towns.empty')}</p> : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
              {towns.map((entry) => (
                <li key={entry.id}>
                  <button type="button" className="hit" disabled={entry.status === 'archived'}
                    onClick={() => useGame.getState().requestEnterTown(entry.id)}
                    style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 6, textAlign: 'left',
                      font: 'inherit', color: 'inherit', background: 'var(--cream-100)', border: 0, borderRadius: 16, boxShadow: 'var(--sh-soft)',
                      padding: '12px 16px', cursor: 'pointer' }}>
                    <span style={{ fontWeight: 900, fontSize: 20 }}>{entry.name}</span>
                    <span>{entry.role === 'mayor' && <Tag color={entry.color} dot>{t('towns.mayor')}</Tag>}</span>
                    <span style={{ gridColumn: '1 / -1', display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 14 }}>
                      <span>{t('towns.mayorOf', { name: entry.mayorName })}</span>
                      <span>{t('towns.residents', { n: entry.residents })}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {/* Always shown: the founding cap is the server's (D-46); a 429 too_many_towns toasts on the found screen. */}
          <Button onClick={() => useGame.getState().openScreen('foundTown', undefined)}>{t('towns.found')}</Button>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button variant="quiet" onClick={() => useGame.getState().openScreen('creator', undefined)}>{t('creator.editTitle')}</Button>
            <Button variant="quiet" onClick={() => void useGame.getState().signOut()}>{t('hud.signOut')}</Button>
          </div>
        </div>
      </Panel>
    </div>
  );
}
export const screen: ScreenDef<'towns'> = { id: 'towns', layer: 'full', component: Towns };
